import React, { useEffect, useState, useCallback } from "react";
import * as Icons from "lucide-react";
import api, { fmtDate } from "@/lib/api";
import { toast } from "sonner";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RequestDetail } from "@/pages/hris/ApprovalQueue";

const CFG = {
  wfh: { title: "Work From Home", icon: "Home", fields: ["range"] },
  official_business: { title: "Official Business", icon: "Briefcase", fields: ["ob"] },
  dtr_correction: { title: "DTR Correction", icon: "FileClock", fields: ["dtrc"] },
  overtime: { title: "Overtime", icon: "Timer", fields: ["ot"] },
};
const STATUS = { Pending: "bg-amber-50 text-amber-700", Approved: "bg-emerald-50 text-emerald-700", Rejected: "bg-rose-50 text-rose-700", Cancelled: "bg-slate-100 text-slate-500" };

export default function RequestForm({ type }) {
  const cfg = CFG[type];
  const [form, setForm] = useState({});
  const [mine, setMine] = useState([]);
  const [stF, setStF] = useState("All");
  const [busy, setBusy] = useState(false);
  const [approver, setApprover] = useState(null);
  const [clockOpen, setClockOpen] = useState(false);

  const load = useCallback(() => api.get(`/hris/requests?type=${type}`).then(({ data }) => setMine(data)).catch(() => {}), [type]);
  useEffect(() => { setForm(type === "dtr_correction" ? { dtr_type: "both" } : {}); load(); }, [type, load]);
  useEffect(() => { if (["dtr_correction", "official_business"].includes(type)) api.get("/hris/my-approver").then(({ data }) => setApprover(data)).catch(() => {}); }, [type]);
  useEffect(() => {
    if (type !== "official_business") return;
    api.get("/attendance/today").then(({ data }) => setClockOpen(!!(data && data.clock_in && !data.clock_out))).catch(() => {});
  }, [type]);

  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/hris/requests/upload", fd); setForm((f) => ({ ...f, attachment_url: data.url })); toast.success("Attached"); }
    catch (e) { toast.error("Upload failed"); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/hris/requests", { type, ...form });
      toast.success("Request submitted"); setForm({}); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Submission failed"); }
    setBusy(false);
  };

  return (
    <div className="uemp-fade-in">
      <PageHeader title={cfg.title} subtitle="Submit a request — it routes to your manager / HR for approval." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="request-form">
          {cfg.fields.includes("range") && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>From</Label><Input type="date" value={form.date_from || ""} onChange={(e) => setForm({ ...form, date_from: e.target.value })} data-testid="rf-date-from" /></div>
              <div className="space-y-2"><Label>To</Label><Input type="date" value={form.date_to || ""} onChange={(e) => setForm({ ...form, date_to: e.target.value })} data-testid="rf-date-to" /></div>
            </div>
          )}
          {cfg.fields.includes("location") && (
            <div className="space-y-2"><Label>Location / Destination</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="rf-location" /></div>
          )}
          {cfg.fields.includes("dtrc") && (
            <>
              <div className="flex items-center gap-3 pb-1" data-testid="rf-approver">
                <Label className="w-24 shrink-0 text-slate-500">Approver</Label>
                <span className="text-sm font-medium text-slate-800">{approver?.name || "—"}</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="rf-target-date" /></div>
                <div className="space-y-2">
                  <Label>DTR Type</Label>
                  <Select value={form.dtr_type || "both"} onValueChange={(v) => setForm({ ...form, dtr_type: v, ...(v === "time_in" ? { correct_time_out: "" } : v === "time_out" ? { correct_time_in: "" } : {}) })}>
                    <SelectTrigger data-testid="rf-dtr-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="both" data-testid="rf-dtr-type-both">Both Time-In and Time-Out</SelectItem>
                      <SelectItem value="time_in" data-testid="rf-dtr-type-in">Time-In Only</SelectItem>
                      <SelectItem value="time_out" data-testid="rf-dtr-type-out">Time-Out Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(form.dtr_type || "both") !== "time_out" && (
                  <div className="space-y-2"><Label>Time-In</Label><Input type="datetime-local" value={form.correct_time_in || ""} onChange={(e) => setForm({ ...form, correct_time_in: e.target.value })} data-testid="rf-time-in" /></div>
                )}
                {(form.dtr_type || "both") !== "time_in" && (
                  <div className="space-y-2"><Label>Time-out</Label><Input type="datetime-local" value={form.correct_time_out || ""} onChange={(e) => setForm({ ...form, correct_time_out: e.target.value })} data-testid="rf-time-out" /></div>
                )}
              </div>
            </>
          )}
          {cfg.fields.includes("ot") && (
            <>
              <div className="space-y-2"><Label>Overtime Date</Label><Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="rf-ot-date" /></div>
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2"><Label>Start Time</Label><Input type="time" value={form.start_time || ""} onChange={(e) => setForm({ ...form, start_time: e.target.value })} data-testid="rf-ot-start" /></div>
                <div className="space-y-2"><Label>End Time</Label><Input type="time" value={form.end_time || ""} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="rf-ot-end" /></div>
                <div className="space-y-2"><Label>Hours</Label><Input type="number" step="0.5" min="0" value={form.hours ?? ""} onChange={(e) => setForm({ ...form, hours: Number(e.target.value) })} data-testid="rf-ot-hours" /></div>
              </div>
            </>
          )}
          {cfg.fields.includes("ob") && (
            <>
              <div className="flex items-center gap-3 pb-1" data-testid="rf-approver">
                <Label className="w-24 shrink-0 text-slate-500">Approver</Label>
                <span className="text-sm font-medium text-slate-800">{approver?.name || "—"}</span>
              </div>
              {clockOpen && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800 flex items-start gap-2" data-testid="rf-clock-warning">
                  <Icons.AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  You are currently clocked in. Please clock out in Attendance before filing an Official Business.
                </div>
              )}
              <div className="space-y-2"><Label>Date</Label><Input type="date" value={form.target_date || ""} onChange={(e) => setForm({ ...form, target_date: e.target.value })} data-testid="rf-target-date" /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Time in</Label><Input type="datetime-local" value={form.correct_time_in || ""} onChange={(e) => setForm({ ...form, correct_time_in: e.target.value })} data-testid="rf-time-in" /></div>
                <div className="space-y-2"><Label>Time out</Label><Input type="datetime-local" value={form.correct_time_out || ""} onChange={(e) => setForm({ ...form, correct_time_out: e.target.value })} data-testid="rf-time-out" /></div>
              </div>
              <div className="space-y-2"><Label>Destination</Label><Input value={form.location || ""} onChange={(e) => setForm({ ...form, location: e.target.value })} data-testid="rf-location" /></div>
              <div className="space-y-2"><Label>Person / Company to See</Label><Input value={form.person_to_see || ""} onChange={(e) => setForm({ ...form, person_to_see: e.target.value })} data-testid="rf-person-to-see" /></div>
            </>
          )}
          <div className="space-y-2"><Label>{type === "official_business" ? "Purpose / Remarks" : "Reason"}</Label><Textarea rows={3} value={form.reason || ""} onChange={(e) => setForm({ ...form, reason: e.target.value })} data-testid="rf-reason" /></div>
          <div className="space-y-2"><Label>Attachment (optional)</Label><Input type="file" onChange={(e) => upload(e.target.files[0])} data-testid="rf-attachment" />{form.attachment_url && <span className="text-xs text-emerald-600">File attached ✓</span>}</div>
          <Button onClick={submit} disabled={busy || (type === "official_business" && clockOpen)} className="bg-blue-600 hover:bg-blue-700 w-full" data-testid="rf-submit">{busy ? "Submitting…" : "Submit Request"}</Button>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="flex flex-wrap items-center gap-1.5 mb-3"><div className="text-sm font-medium text-slate-800 mr-2">My Requests</div>
            {["All", "Pending", "Approved", "Rejected", "Cancelled"].map((s) => <button key={s} onClick={() => setStF(s)} className={`text-[11px] rounded-full px-2 py-0.5 border ${stF === s ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`} data-testid={`my-requests-filter-${s}`}>{s} {s === "All" ? mine.length : mine.filter((r) => r.status === s).length}</button>)}
          </div>
          <div className="space-y-2 max-h-[420px] overflow-y-auto" data-testid="my-requests">
            {mine.length === 0 && <div className="text-sm text-slate-400 py-6 text-center">No requests yet.</div>}
            {mine.filter((r) => stF === "All" || r.status === stF).map((r) => (
              <div key={r.id} className="border border-slate-100 rounded-lg p-3" data-testid="my-request-row">
                <div className="flex items-center justify-between">
                  <Badge className={`${STATUS[r.status]} text-[10px]`}>{r.status}</Badge>
                  <span className="text-[11px] text-slate-400">{fmtDate(r.created_at)}</span>
                </div>
                <div className="text-sm text-slate-700 mt-1">{r.target_date ? `For ${fmtDate(r.target_date)}` : `${r.date_from ? fmtDate(r.date_from) : ""}${r.date_to ? " → " + fmtDate(r.date_to) : ""}`}</div>
                {r.reason && <div className="text-xs text-slate-500 mt-0.5 line-clamp-2">{r.reason}</div>}
                <RequestDetail r={r} />
                {r.status === "Pending" && <button onClick={async () => { await api.post(`/hris/requests/${r.id}/cancel`); load(); }} className="text-xs text-rose-600 mt-1" data-testid="my-request-cancel">Cancel</button>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
